import React from 'react'
import { BetCard } from '@mobi/betslip/Components/Common/BetCard'
import { RacingTitle } from '@mobi/betslip/Components/Common/BetCard/Components/EventDetails/Components/RacingTitle'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors, spacing } from '@mobi/component-library/Foundations'
import { Icon as IconLegacy } from '@mobi/component-library/Legacy/Icon'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { SilkFallback } from '@core/Areas/Account/Pages/Activity/Components/Ticket/Components/Common'
import {
  TicketLayoutType,
  getLegStatus,
  getNumberFromString,
  getSingleTicketTitle,
  shouldUseFallbackIcon,
} from '@core/Areas/Account/Pages/Activity/Components/Ticket/helpers'
import { TicketDisplayContext } from '@core/Areas/Account/Pages/Activity/context'
import type { Ticket } from '@core/Areas/Account/Pages/Activity/types'
import { trackBetLoopNavigateToRace } from '@core/Areas/BetLoop/helpers/analytics'
import type { BetLoopShareTicket } from '@core/Areas/BetLoop/types'
import { getIconTypeFromMeetingCode } from '@core/Areas/Quickbet/helpers/IconTypeHelper'
import { RunnerNumber } from '@core/Areas/RaceCard/Components/RunnerNumber'
import { RaceCodesLowerCase } from '@core/Areas/Racing/Types/MeetingInformation'
import { getRaceLink, meetingIdToContestType } from '@core/Areas/Racing/helpers'
import { SilkImageContained } from '@core/Components/SilkImage/SilkImageContained'
import { CountdownStatus } from './Components/CountdownStatus'

export const Multi: React.FC<{
  ticket: Ticket | BetLoopShareTicket
  ticketLayoutType: TicketLayoutType
}> = ({ ticket, ticketLayoutType }) => {
  const isTipping = ticketLayoutType === 'tipping'

  const ticketContext = React.useContext(TicketDisplayContext)
  const isContextShare = ticketContext.type === 'share'
  const isBetLoopShare = isContextShare && ticketContext.data.display === 'bet-loop'

  return (
    <Flex flexDirection='column' gap={spacing.smx1}>
      {ticket.legs.map(leg => {
        const shouldHideSilk = shouldUseFallbackIcon(leg, ticket.planSeq)

        const shouldProvideLink = !!(leg.fixtureId && leg.fixtureDate && leg.raceNumber)

        const runnerCode = meetingIdToContestType(leg.fixtureId, true)
        const { title, runnerNumber } = getSingleTicketTitle(leg, ticket.planSeq, true)

        const runnerNameForBlackbook =
          runnerNumber &&
          runnerCode &&
          leg.selections[0]?.selectionDetails[0]?.acceptorDetails[0]?.name
        const shouldRenderBlackbookNumber = runnerCode && runnerNameForBlackbook && runnerNumber

        const linkInfo = shouldProvideLink
          ? {
              pathname: getRaceLink({
                meetingDate: leg.fixtureDate,
                meetingId: leg.fixtureId,
                raceNumber: leg.raceNumber,
                isFOO: false,
              }),
              handleNavigationTracking: isBetLoopShare
                ? () => trackBetLoopNavigateToRace(ticket)
                : undefined,
            }
          : undefined

        const selectionSubHeading = (
          leg.eventNameLine2 ||
          leg.selections[0].header ||
          leg.betType
        )?.replace('-Fixed', '')

        const legStatus = getLegStatus(leg.result)

        return (
          <BetCard.InnerContainerStyled key={leg.header} data-testid='Multi.Details.Leg'>
            <BetCard.EventDetails
              iconComponent={
                <IconLegacy
                  size='1.6rem'
                  type={
                    getIconTypeFromMeetingCode(
                      leg.eventType || leg.fixtureId?.charAt(leg.fixtureId.length - 1)
                    ) || 'tab'
                  }
                />
              }
              heading={
                isTipping ? (
                  leg.eventName
                ) : leg.raceNumber && leg.meetingName ? (
                  <RacingTitle
                    races={[{ raceNumber: leg.raceNumber }]}
                    meetingName={leg.meetingName}
                    isMultipleRaceSelection={false}
                  />
                ) : (
                  `${leg.sportName ? leg.sportName + ' ' : ''}${leg.eventName}`
                )
              }
              statusComponent={<CountdownStatus multiLeg={leg} />}
              linkInfoPrimary={linkInfo}
            />

            <BetCard.SelectionInfo
              heading={
                <>
                  {shouldRenderBlackbookNumber ? (
                    <>
                      <RunnerNumber
                        starterType={runnerCode}
                        starterName={runnerNameForBlackbook}
                        starterNumber={runnerNumber}
                      />
                      <span>&nbsp;-&nbsp;</span>
                    </>
                  ) : runnerNumber ? (
                    `${runnerNumber} - `
                  ) : (
                    ''
                  )}
                  {title}
                </>
              }
              subHeading={selectionSubHeading}
              priceWin={getStarterPrice(leg)}
              rightAlignedComponent={
                legStatus === 'Won' ? (
                  <Flex alignItems='center' testId='multi-leg-win'>
                    <Icon name='SolidCheckCircle' size='2rem' color={colors.success[400]} />
                  </Flex>
                ) : legStatus === 'Lost' ? (
                  <Flex alignItems='center' testId='multi-leg-loss'>
                    <Icon name='SolidXCircle' size='2rem' color={colors.error[600]} />
                  </Flex>
                ) : null
              }
              iconComponent={
                shouldHideSilk ? (
                  <SilkFallback contestType={runnerCode?.toLowerCase() as RaceCodesLowerCase} />
                ) : (
                  <SilkImageContained
                    fixtureDate={leg.fixtureDate}
                    fixtureId={leg.fixtureId}
                    raceNumber={leg.selections[0].raceNumber || leg.raceNumber}
                    acceptorNumber={leg.selections[0].selectionDetails[0].acceptorDetails[0].number}
                  />
                )
              }
              linkInfo={linkInfo}
            />
          </BetCard.InnerContainerStyled>
        )
      })}
    </Flex>
  )
}

// =============
// Local Helpers
// =============

function getStarterPrice(leg: Ticket['legs'][0]) {
  const isPlace = leg.betType?.toLowerCase().includes('place')
  const returnRate = isPlace ? (leg.return?.split(',')[1] ?? leg.return) : leg.return
  return returnRate ? getNumberFromString(returnRate) : undefined
}
